import type { CallRecord } from '@/features/calls/types'
import type { ClientDuty } from '@/features/patients/hooks/useClientDutiesQuery'
import { callReach } from './callReach'

/**
 * Last ten digits of a phone number, so "+1 (415) 555-0182", "4155550182" and
 * the calls API's numeric 14155550182 compare equal.
 */
export function phoneKey(value: string | number | null | undefined): string {
  return String(value ?? '')
    .replace(/\D/g, '')
    .slice(-10)
}

function newestFirst(calls: CallRecord[]): CallRecord[] {
  return calls.toSorted(
    (a, b) =>
      new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  )
}

/**
 * Per number, how many of the latest calls in a row went unanswered. The run
 * stops at the first call that reached someone, so a number that was answered
 * yesterday shows nothing however often it rang out before. Failed, pending
 * and unknown calls say nothing about the number, so they neither count nor
 * end the run.
 */
export function unansweredStreaks(calls: CallRecord[]): Map<string, number> {
  const streaks = new Map<string, number>()
  const settled = new Set<string>()

  for (const call of newestFirst(calls)) {
    const key = phoneKey(call.phone)
    if (!key || settled.has(key)) continue
    const reach = callReach(call)
    if (reach === 'unanswered') {
      streaks.set(key, (streaks.get(key) ?? 0) + 1)
    } else if (reach === 'connected') {
      settled.add(key)
    }
  }
  return streaks
}

/** The most recent call that reached the client, or null if none did. */
export function lastContact(calls: CallRecord[]): CallRecord | null {
  return (
    newestFirst(calls).find((call) => callReach(call) === 'connected') ?? null
  )
}

export interface DutyCounts {
  /** Still being worked, on a duty that has not been archived. */
  open: number
  /** Assignments relay counts as done. */
  done: number
}

export function dutyCounts(duties: ClientDuty[]): DutyCounts {
  return {
    open: duties.filter(
      (duty) => !duty.is_done && duty.duty_lifecycle_status !== 'archived'
    ).length,
    done: duties.filter((duty) => duty.is_done).length,
  }
}
